import { z } from 'zod/v4';

import { TreeSettingsSchema } from '@/shared/lib/utils/tree/schema';

/** Источник данных колонки отчёта. */
export const ReportSourceDTO = z.enum(['metrics', 'documents']);
export type ReportSource = z.infer<typeof ReportSourceDTO>;

/** Что со стороны КЕ участвует в связи: поле КЕ или её атрибут. */
export const ReportJoinKindDTO = z.enum(['field', 'attribute']);
export type ReportJoinKind = z.infer<typeof ReportJoinKindDTO>;

/** Свёртка нескольких значений, пришедших на один ключ. */
export const ReportAggregationDTO = z.enum([
  'sum',
  'avg',
  'min',
  'max',
  'count',
]);
export type ReportAggregation = z.infer<typeof ReportAggregationDTO>;

/**
 * Часть ключа связи: поле или атрибут КЕ = метка метрики либо поле документа.
 * `node_id` — узел, с чьей КЕ берётся значение: пусто у самой КЕ, иначе предок по композиции.
 */
export const ReportJoinPartDTO = z.object({
  kind: ReportJoinKindDTO,
  node_id: z.string().nullish(),
  ci_key: z.string(),
  source_key: z.string(),
});
export type ReportJoinPart = z.infer<typeof ReportJoinPartDTO>;

/** Ключ связи целиком: части складываются в один ключ, совпасть должны все. */
export const ReportJoinDTO = z.array(ReportJoinPartDTO);
export type ReportJoin = z.infer<typeof ReportJoinDTO>;

/** ReportColumnDTO */
export const ReportColumnDTO = z.object({
  key: z.string(),
  label: z.string(),
  source: ReportSourceDTO,
  query: z.string(),
  join: ReportJoinDTO.nullish(),
  index: z.string().nullish(),
  aggregation: ReportAggregationDTO.optional(),
  total: ReportAggregationDTO.nullish(),
  value_path: z.string().optional(),
});
export type ReportColumn = z.infer<typeof ReportColumnDTO>;

/** ReportGroupDTO: подстроки под строкой КЕ, собранные из меток источника. */
export const ReportGroupDTO = z.object({
  key: z.string(),
  label: z.string(),
  subrow_keys: z.array(z.string()),
  title_key: z.string().nullish(),
  columns: z.array(ReportColumnDTO),
});
export type ReportGroup = z.infer<typeof ReportGroupDTO>;

/** ReportSubrowValuesDTO */
export const ReportSubrowValuesDTO = z.object({
  key: z.string(),
  title: z.string(),
  values: z.record(z.string(), z.number()),
});
export type ReportSubrowValues = z.infer<typeof ReportSubrowValuesDTO>;

/** ReportGroupValuesDTO */
export const ReportGroupValuesDTO = z.object({
  key: z.string(),
  label: z.string(),
  subrows: z.array(ReportSubrowValuesDTO),
});
export type ReportGroupValues = z.infer<typeof ReportGroupValuesDTO>;

/** CIReportValuesDTO */
export const CIReportValuesDTO = z.object({
  values: z.record(z.string(), z.number()),
  groups: z.array(ReportGroupValuesDTO),
});
export type CIReportValues = z.infer<typeof CIReportValuesDTO>;

/**
 * ReportValuesDTO: значения по КЕ, колонки без ответа источника и колонки,
 * у которых ключ связи совпал у нескольких КЕ.
 */
export const ReportValuesDTO = z.object({
  cis: z.record(z.string(), CIReportValuesDTO),
  failed_columns: z.array(z.string()),
  ambiguous_columns: z.array(z.string()).optional(),
});
export type ReportValues = z.infer<typeof ReportValuesDTO>;

/**
 * Узел настройки таблицы: та же схема, что у настроек дерева, плюс колонки отчёта.
 * Бэкенд хранит их в том же table_settings узла TQL.
 */
export type TableSettingsNode = TreeSettingsSchema & {
  ci_type_name?: string;
  is_visible?: boolean;
  report_columns?: ReportColumn[];
  report_groups?: ReportGroup[];
  children?: TableSettingsNode[];
};
